import { FC } from "react";
import { useTranslation } from "react-i18next";
import Check from "../../../assets/svg/check-ring.svg";
import Unhealthy from "../../../assets/svg/unhealthy.svg";
import { AdminFormFieldDropdown } from "../../../components/admin/formFields";
import CAM_K220 from "../../../assets/images/cam_K220.png";
import CAM_K145C from "../../../assets/images/cam_K145C_K245C.png";
import CAM_K165 from "../../../assets/images/cam_K165_K265.png";
import {
  CameraDetail,
  DeviceReportSimple,
  HealthCams,
} from "../../../api/types/CamerasAdminVM";
import Tooltip from "../../../components/cameraAdmin/tooltip";
import ParkingPhotos from "./modalParkingPhotos";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { useGetLastParkingPhotoQuery } from "../../../api/network/vmApiService";
import svgIcons from "../../../assets/svg/svgIcons";
import { toast } from "react-toastify";
import { CAM_TYPE } from "../../../constants/constants";

interface ScreenCameraHealthProps {
  dataDeviceDetails: CameraDetail | null;
  healthCams: HealthCams | null;
  simpleHealthData: DeviceReportSimple | null;
}

const ScreenCameraHealth: FC<ScreenCameraHealthProps> = ({
  dataDeviceDetails,
  healthCams,
  simpleHealthData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras.camera_details.tabs.health",
  });
  const dispatch = useDispatch();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const { data: parkingPhotoResponse } = useGetLastParkingPhotoQuery(
    dataDeviceDetails?.cid ?? "",
    {
      skip: !dataDeviceDetails?.cid,
      refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
    }
  );

  const simpleReportData = simpleHealthData?.report;
  const camerahealth = simpleReportData?.camera;

  const sanitizeCameraIssueDescrtion = (description: string) => {
    return description
      .replace("[embedded_inward]", "Driver Facing Camera")
      .replace("[embedded_outward]", "Road Facing Camera");
  };

  const getInwardHealth = () => {
    if (!camerahealth) {
      return {
        status: "",
        title: "Inward Camera",
        description: "",
      };
    }
    const { embedded_inward } = camerahealth;
    let title = "Healthy";
    const status =
      embedded_inward.status === "unhealthy" ? "unhealthy" : "healthy";
    if (status === "unhealthy") {
      let description = `${embedded_inward.description} ${embedded_inward.solution}`;
      description = sanitizeCameraIssueDescrtion(description);
      title = "Unhealthy";
      return {
        status,
        title,
        description,
      };
    } else {
      return {
        status,
        title: "Healthy",
        description: "Inward camera is healthy.",
      };
    }
  };

  const getOutwardHealth = () => {
    if (!camerahealth) {
      return {
        status: "",
        title: "Outward Camera",
        description: "",
      };
    }
    const { embedded_outward } = camerahealth;
    let title = "Healthy";
    const status =
      embedded_outward.status === "unhealthy" ? "unhealthy" : "healthy";
    if (status === "unhealthy") {
      let description = `${embedded_outward.description} ${embedded_outward.solution}`;
      description = sanitizeCameraIssueDescrtion(description);
      title = "Unhealthy";
      return {
        status,
        title,
        description,
      };
    } else {
      return {
        status,
        title: "Healthy",
        description: "Outward camera is healthy.",
      };
    }
  };

  const getDMSHealth = () => {
    const title = "DMS";
    if (!healthCams) {
      return {
        status: "",
        title,
        description: "",
      };
    }
    if (healthCams?.embedded_inward?.dms_ai_invalid === "Yes") {
      return {
        status: "unhealthy",
        title: "DMS Failure",
        description:
          "DMS could not be calibrated. Please inspect camera lens’s mounting status for any obstruction or incorrect angle settings.",
      };
    } else {
      return {
        status: "healthy",
        title,
        description: "DMS is healthy.",
      };
    }
  };

  const getAdasHealth = () => {
    const title = "ADAS";
    if (!healthCams) {
      return {
        status: "",
        title,
        description: "",
      };
    }
    if (healthCams?.embedded_outward?.adas_ai_invalid === "Yes") {
      return {
        status: "unhealthy",
        title: "ADAS Failure",
        description:
          "ADAS could not be calibrated. Please inspect camera lens’s mounting status for any obstruction or incorrect angle settings.",
      };
    } else {
      return {
        status: "healthy",
        title,
        description: "ADAS is healthy.",
      };
    }
  };

  const getUVCHealth = () => {
    if (!healthCams) {
      return {
        status: "",
        title: "UVC Camera",
        description: "",
      };
    }

    let status = healthCams?.uvc?.status || "";
    if (status === "No Equipped") {
      return {
        status: "not_equipped",
        title: "Not equipped",
        description: "UVC camera is not equipped.",
      };
    } else if (status === "unhealthy") {
      return {
        status: "unhealthy",
        title: "UVC Camera",
        description:
          "UVC camera is unhealthy. Please inspect camera lens’s mounting status for any obstruction or incorrect angle settings.",
      };
    } else {
      return {
        status: "healthy",
        title: "UVC Camera",
        description: "UVC camera is healthy.",
      };
    }
  };

  const getUvcAIHealth = () => {
    if (!healthCams || healthCams?.uvc?.status === "No Equipped") {
      return {
        status: "not_equipped",
        title: "Not equipped",
        description: "",
      };
    }
    if (healthCams?.uvc?.ai_invalid === "Yes") {
      return {
        status: "unhealthy",
        title: "UVC AI Failure",
        description:
          "UVC AI could not be calibrated. Please inspect camera lens’s mounting status for any obstruction or incorrect angle settings.",
      };
    } else {
      return {
        status: "healthy",
        title: "UVC AI",
        description: "UVC AI is healthy.",
      };
    }
  };

  /**
   * Method to return camera image by its model type
   * @param modelName
   * @returns Image of camera
   */
  const getCameraImageByModel = (modelName: string) => {
    const upperModel = modelName.toUpperCase();
    switch (upperModel) {
      case CAM_TYPE.K220:
        return CAM_K220;
      case CAM_TYPE.K145C:
      case CAM_TYPE.K245C:
        return CAM_K145C;
      case CAM_TYPE.K165:
      case CAM_TYPE.K265:
        return CAM_K165;
      default:
        return "";
    }
  };

  const EmbeddedCameraInfo = [
    { title: t("type"), value: [t("inward"), t("outward")] },
    {
      title: t("camera_health"),
      value: [getInwardHealth(), getOutwardHealth()],
    },
    {
      title: t("adas/dms_ai_health"),
      value: [getDMSHealth(), getAdasHealth()],
    },
  ];

  const UvcCameraInfo = [
    { title: t("camera_health"), value: [getUVCHealth()] },
    { title: t("adas/dms_ai_health"), value: [getUvcAIHealth()] },
  ];

  const CabinInfo = [
    {
      title: t("driver_side_in_cabin"),
      value: dataDeviceDetails?.driver_position,
    },
    {
      title: t("where_is_the_in_cabin_camera"),
      value: dataDeviceDetails?.install_position === "L" ? "Left" : "Right",
    },
  ];

  const onParkingPhotoClick = () => {
    if (
      parkingPhotoResponse?.data?.snapshot &&
      parkingPhotoResponse?.data?.snapshot?.length > 0
    ) {
      dispatch(setModalsData({ ...modalsState, showParkingPhotoView: true }));
    } else {
      toast.error("No parking photo available.");
    }
  };

  return (
    <div className="col-span-9 space-y-4">
      <h1 className="font-semibold text-base leading-none align-middle col-span-9">
        {t("embedded_camera")}
      </h1>
      <div className="bg-gray-100 col-span-9 grid grid-cols-9 rounded-lg p-4 mt-2">
        {EmbeddedCameraInfo.map(
          ({ title, value }: { title: string; value: any }, index) => (
            <div key={index} className="col-span-3">
              <h2 className="font-semibold text-sm leading-5 tracking-tight align-middle">
                {title}
              </h2>
              <div className="border-b border-dotted border-gray-400 w-full my-2" />
              <div className="text-sm leading-5 tracking-tight space-y-6">
                {value.map((val: any, idx: number) => {
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-2 ${title === "Type" ? "font-semibold" : "font-medium"}`}
                    >
                      {title === "Type" ? (
                        val
                      ) : (
                        <>
                          <img
                            src={val?.status === "healthy" ? Check : Unhealthy}
                            className="h-5 w-5 mr-2"
                            alt="health"
                          />
                          <span className="font-medium text-sm">
                            {val?.title}
                          </span>
                          {val?.status === "unhealthy" && (
                            <Tooltip description={val?.description} />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}
      </div>

      <h1 className="font-semibold text-base leading-none align-middle col-span-9">
        {t("auxiliary_camera")}
      </h1>
      <div className="bg-gray-100 col-span-9 grid grid-cols-6 rounded-lg p-4 mt-2">
        {UvcCameraInfo.map(
          ({ title, value }: { title: string; value: any }, index) => (
            <div key={index} className="col-span-3">
              <h2 className="font-semibold text-sm leading-5 tracking-tight align-middle">
                {title}
              </h2>
              <div className="border-b border-dotted border-gray-400 w-full my-2" />
              <div className="text-sm leading-5 tracking-tight space-y-6">
                {value.map((val: any, idx: number) => {
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-2 ${title === "Type" ? "font-semibold" : "font-medium"}`}
                    >
                      {title === "Type" ? (
                        val
                      ) : (
                        <>
                          {val?.status !== "not_equipped" && (
                            <img
                              src={
                                val?.status === "healthy" ? Check : Unhealthy
                              }
                              className="h-5 w-5 mr-2"
                              alt="health"
                            />
                          )}
                          <span className="font-medium text-sm">
                            {val?.title}
                          </span>
                          {val?.status === "unhealthy" && (
                            <Tooltip description={val?.description} />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}
      </div>

      <h1 className="font-semibold text-base leading-none align-middle col-span-9">
        {t("mounting_information")}
      </h1>
      <div className="bg-gray-100 col-span-9 grid grid-cols-9 rounded-lg p-4 mt-2 space-y-6">
        <div className="col-span-9 flex flex-col font-semibold text-sm leading-5 tracking-tight align-middle gap-2">
          <h1>{t("current_mounting")}</h1>

          <button
            className="group underline decoration-solid underline-offset-0 text-accent-blue-bright w-20"
            type="button"
            onClick={onParkingPhotoClick}
          >
            <span className="flex gap-2 items-center w-48">
              <img
                src={svgIcons.ShowImageIcon}
                alt="show-image"
                width={32}
                className={`p-2 bg-gray-200 cursor-pointer rounded-md group-hover:bg-accent-blue-pale`}
              />
              {t("see_image")}
            </span>
          </button>
        </div>

        <div className="col-span-9 grid grid-cols-2">
          <AdminFormFieldDropdown
            id="mounting_height"
            label={t("mounting_height")}
            name="mounting_height"
            customWrapperClass="col-span-1"
            customSelectboxClass="mt-2"
            value={String(dataDeviceDetails?.camera_height)}
            options={[
              {
                label: String(dataDeviceDetails?.camera_height),
                value: String(dataDeviceDetails?.camera_height),
              },
            ]}
            readOnly={true}
          />
        </div>

        <div className="col-span-9">
          <h1 className="font-semibold text-sm leading-5 tracking-tight align-middle">
            {t("in_cabin_camera_location")}
          </h1>
          <div className="w-full grid grid-cols-2 rounded-lg bg-white px-4 mt-2">
            <div className="col-span-1 flex flex-col">
              {CabinInfo.map((item, index) => (
                <div
                  key={index}
                  className="font-semibold text-sm leading-5 tracking-tight align-middle mt-2"
                >
                  <h1>{item.title}</h1>
                  <span className="font-normal">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="col-span-1">
              <img
                src={getCameraImageByModel(dataDeviceDetails?.sku_name ?? "")}
                alt="Camera"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreenCameraHealth;
